// Adapted from pn-www's `utils/hello-extension.js`.

import {accessTokenUuid, accountUuid, projectUuid} from "../../main/data/model/index.js";
import {ControlledPromise} from "../../main/utils/async.js";

interface credentials {
	accountUuid:  accountUuid;
	accessToken:  accessTokenUuid;
	projectUuid?: projectUuid;
};

type messageId =
	"HELLO_EXT"
	| "HELLO_WEBAPP"
	| "PN_INTEGRATION_TAKEOVER" // Tell pn-browser-ext that we're taking over for this tab
	| "PN_EXT_NOT_INSTALLED"
	| "GET_AUTH_DATA_RESP"
	| "GET_AUTH_DATA"; // deprecated
type messageType = "FROM_EXT" | "FROM_PAGE";
interface message {
	type:      messageType;
	id:        messageId;
	authData?: { // deprecated
		account: {
			uuid: accountUuid;
		};
		accessToken: accessTokenUuid;
	};
	account?: {
		uuid: accountUuid;
	};
	access_token?:      accessTokenUuid;
	project_uuid?:      projectUuid;
	ext_authenticated?: boolean;
};

const helloIntervalMs = 150;
const maxTries = 3;

class ExtensionComm {
	extensionAvailable = false;
	timeoutHello: number;
	helloCt = 0;
	promise: {
		resolve:    (data: credentials) => void;
		reject:     (reason: string) => void;
		start:      () => Promise<credentials>;
		getPromise: () => Promise<credentials>;
		isPending:  () => boolean;
	};

	constructor() {
		if (typeof window !== "undefined")
			window.addEventListener("message", this.messageHandler.bind(this));
	}

	private receivedAuthFromExtension(credentials: credentials) {
		this.promise?.resolve(credentials);
	}

	private receivedNoAuthFromExtension() {
		this.promise?.reject("PN: Extension not authenticated.");
	}

	private extensionNeverResponded() {
		this.promise?.reject("PN: Cannot communicate with extension.");
	}

	private messageHandler(evt: MessageEvent<message>) {
		// only listen to the current window
		if (evt.source !== window)
			return;
		// only listen to messages from the extension
		if (!evt.data.type || evt.data.type !== "FROM_EXT")
			return;

		// We know the extension is alive
		this.extensionAvailable = true;

		switch (evt.data.id) {
			// Extension replies to our HELLO_EXT
			case "HELLO_WEBAPP":
				if (this.timeoutHello)
					clearTimeout(this.timeoutHello);

				if (evt.data.ext_authenticated) {
					if (evt.data.account === undefined || evt.data.access_token === undefined) {
						// This is an older version of the extension. Let's use the deprecated GET_AUTH_DATA msg.
						this.sendMessage({type: "FROM_PAGE", id: "GET_AUTH_DATA"});
						break;
					}

					const credentials: credentials = {
						accountUuid: evt.data.account.uuid,
						accessToken: evt.data.access_token
					};

					if (evt.data.project_uuid !== undefined)
						credentials.projectUuid = evt.data.project_uuid;

					this.receivedAuthFromExtension(credentials);
				} else
					this.receivedNoAuthFromExtension();

				break;

			// Extension replies to our GET_AUTH_DATA (deprecated)
			case "GET_AUTH_DATA_RESP":
				if (evt.data.authData === undefined)
					break;

				this.receivedAuthFromExtension({
					accountUuid: evt.data.authData.account.uuid,
					accessToken: evt.data.authData.accessToken
				});
				break;
		}
	}

	sendMessage(msg: message) {
		if (typeof window === "undefined" || !window.postMessage)
			return;

		window.postMessage(msg, "*");
	}

	private sayHelloLoop() {
		if (this.extensionAvailable)
			return;
		if (this.helloCt === maxTries) {
			this.extensionNeverResponded();
			return; // bail out after maxTries (maybe no extension is installed)
		}

		this.helloCt++;
		this.sendMessage({type: "FROM_PAGE", id: "HELLO_EXT"});

		this.timeoutHello = setTimeout(this.sayHelloLoop.bind(this), helloIntervalMs) as unknown as number;
	}

	async getCredsFromExtension(): Promise<credentials> {
		if (typeof window === "undefined" || !window.postMessage)
			throw new Error("No way to communicate with the extension.");

		if (this.promise?.isPending())
			return this.promise.getPromise(); // Another process is already running
		// @ts-expect-error TODO
		this.promise = new ControlledPromise();

		// start the loop
		this.sayHelloLoop();
		// document.addEventListener(
		// 	"DOMContentLoaded",
		// 	() => setTimeout(this.sayHelloLoop, 500)
		// );

		return this.promise.start();
	}

}

export default new ExtensionComm();
